// \src\pages\contato\page.tsx

import { useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useAdmin } from "@/context/AdminContext";
import MathCaptcha from "@/components/base/MathCaptcha";
import PromoCarouselHome from "@/pages/home/components/PromoCarouselHome";

const CONTACT_API_URL = "/api/contato.php";

export default function Contato() {
  const { data } = useAdmin();
  const stores = data.stores;
  const s = data.settings;

  const [formStatus, setFormStatus] = useState<
    "idle" | "sending" | "success" | "error"
  >("idle");
  const [formError, setFormError] = useState("");
  const [captchaOk, setCaptchaOk] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleCaptchaVerify = useCallback(
    (ok: boolean) => setCaptchaOk(ok),
    [],
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("phone_alt") as string || "").trim();

    if (honeypot) {
      setFormStatus("success");
      formRef.current?.reset();
      setCaptchaOk(false);
      return;
    }

    if (!captchaOk) {
      setFormError(
        "Por favor, resolva a verificação de segurança antes de enviar.",
      );
      return;
    }

    setFormStatus("sending");
    setFormError("");

    const payload = new URLSearchParams();

    formData.forEach((value, key) => {
      if (key !== "phone_alt" && typeof value === "string") {
        payload.append(key, value);
      }
    });

    try {
      const res = await fetch(CONTACT_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: payload.toString(),
      });

      const responseText = await res.text();

      let parsed: {
        success?: boolean;
        message?: string;
        code?: string;
        meta?: {
          message?: string;
          detail?: string;
        };
      } = {};

      try {
        parsed = JSON.parse(responseText);
      } catch {
        /* resposta não JSON */
      }

      const serverMsg =
        parsed.message ||
        parsed.meta?.message ||
        parsed.meta?.detail ||
        responseText;

      if (res.ok && (parsed.success === true || parsed.code === "OK")) {
        setFormStatus("success");
        formRef.current?.reset();
        setCaptchaOk(false);
      } else {
        setFormStatus("error");

        setFormError(
          serverMsg?.toLowerCase().includes("spam")
            ? "Mensagem identificada como spam. Tente novamente."
            : serverMsg || "Erro ao enviar. Tente novamente.",
        );
      }
    } catch {
      setFormStatus("error");
      setFormError(
        "Falha de conexão. Verifique sua internet e tente novamente.",
      );
    }
  };

  return (
    <>
      <Navbar />

      <main className="pt-20 min-h-screen bg-background-50">
        <section className="bg-background-200 border-b border-background-300 py-8 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <nav className="text-xs text-foreground-400 mb-3 font-label">
              <Link
                to="/"
                className="hover:text-primary-500 cursor-pointer"
              >
                Início
              </Link>

              <span className="mx-2">/</span>
              <span className="text-primary-500">Contato</span>
            </nav>

            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
              Contato
            </h1>

            <p className="text-foreground-500 text-sm mt-2">
              Estamos aqui para ajudar você em tudo que precisar
            </p>
          </div>
        </section>

        <PromoCarouselHome />

        <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="flex-1">
              <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-2">
                  Envie sua Mensagem
                </h2>

                <p className="text-foreground-500 text-sm mb-6">
                  Preencha o formulário e entraremos em contato em breve.
                </p>

                {formStatus === "success" ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                    <i className="ri-checkbox-circle-line text-4xl text-green-500 mb-3 block" />

                    <h3 className="font-heading font-bold text-green-800 text-lg mb-2">
                      Mensagem Enviada!
                    </h3>

                    <p className="text-green-700 text-sm">
                      Recebemos sua mensagem e entraremos em contato em breve.
                    </p>

                    <button
                      onClick={() => {
                        setFormStatus("idle");
                        setFormError("");
                      }}
                      className="mt-4 bg-green-600 hover:bg-green-700 text-background-50 px-6 py-2 rounded-md text-sm font-label font-medium cursor-pointer transition-all whitespace-nowrap"
                    >
                      Enviar outra mensagem
                    </button>
                  </div>
                ) : (
                  <form
                    ref={formRef}
                    id="contato-form"
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div className="hp-field-alt" aria-hidden="true">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        readOnly
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                          Nome Completo{" "}
                          <span className="text-primary-500">*</span>
                        </label>

                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="Seu nome"
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400 transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                          E-mail{" "}
                          <span className="text-primary-500">*</span>
                        </label>

                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="seu@email.com"
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400 transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                          Telefone / WhatsApp
                        </label>

                        <input
                          type="tel"
                          name="phone"
                          placeholder="(17) 99999-9999"
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400 transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                          Assunto{" "}
                          <span className="text-primary-500">*</span>
                        </label>

                        <select
                          name="subject"
                          required
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                        >
                          <option value="">Selecione...</option>
                          <option value="Orçamento">
                            Solicitar Orçamento
                          </option>
                          <option value="Informações">
                            Informações sobre Produto
                          </option>
                          <option value="Entrega">
                            Entrega e Frete
                          </option>
                          <option value="Parceria">
                            Proposta de Parceria
                          </option>
                          <option value="Reclamação">
                            Reclamação
                          </option>
                          <option value="Outro">
                            Outro Assunto
                          </option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                        Mensagem{" "}
                        <span className="text-primary-500">*</span>
                      </label>

                      <textarea
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        placeholder="Descreva sua dúvida, solicitação ou mensagem..."
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      />

                      <p className="text-xs text-foreground-400 font-label mt-1">
                        Máximo de 500 caracteres
                      </p>
                    </div>

                    <MathCaptcha onVerify={handleCaptchaVerify} />

                    {formError && (
                      <div className="bg-red-50 border border-red-200 rounded-md p-3">
                        <p className="text-red-700 text-sm font-label flex items-center gap-2">
                          <i className="ri-error-warning-line" />
                          {formError}
                        </p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={
                        formStatus === "sending" || !captchaOk
                      }
                      className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 py-3 rounded-md font-label font-semibold text-sm transition-all cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === "sending" ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin" />
                          Enviando...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-send-plane-line" />
                          Enviar Mensagem
                        </span>
                      )}
                    </button>

                    <p className="text-xs text-foreground-400 text-center font-label">
                      <i className="ri-lock-line mr-1" />
                      Seus dados são protegidos e não serão compartilhados.
                    </p>
                  </form>
                )}
              </div>
            </div>

            <div className="w-full lg:w-80 shrink-0 space-y-4">
              <div className="bg-background-50 border border-background-200 rounded-lg p-5">
                <h3 className="font-heading font-bold text-foreground-950 mb-4">
                  Atendimento Rápido
                </h3>

                <div className="space-y-3">
                  {stores.map((store) => (
                    <a
                      key={store.id}
                      href={`https://wa.me/${store.whatsapp}`}
                      target="_blank"
                      rel="nofollow noreferrer"
                      className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-all cursor-pointer"
                    >
                      <div className="w-9 h-9 flex items-center justify-center bg-green-500 text-background-50 rounded-md shrink-0">
                        <i className="ri-whatsapp-line" />
                      </div>

                      <div>
                        <p className="font-label font-semibold text-green-800 text-sm">
                          WhatsApp {store.name}
                        </p>

                        <p className="text-green-600 text-xs">
                          {store.phone.replace(/[()\s-]/g, "")}
                        </p>
                      </div>
                    </a>
                  ))}

                  {s.contactEmail && (
                    <a
                      href={`mailto:${s.contactEmail}`}
                      className="flex items-center gap-3 p-3 bg-background-100 border border-background-200 rounded-md hover:border-primary-300 hover:bg-primary-50 transition-all cursor-pointer"
                    >
                      <div className="w-9 h-9 flex items-center justify-center bg-primary-500 text-background-50 rounded-md shrink-0">
                        <i className="ri-mail-line" />
                      </div>

                      <div className="min-w-0">
                        <p className="font-label font-semibold text-foreground-900 text-sm">
                          E-mail
                        </p>
                        <p className="text-foreground-600 text-xs break-all">
                          {s.contactEmail}
                        </p>
                      </div>
                    </a>
                  )}
                </div>
              </div>

              <div className="bg-primary-50 border border-primary-100 rounded-lg p-5">
                <h3 className="font-heading font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-time-line text-primary-500" />
                  Horário de Atendimento
                </h3>

                <div className="space-y-2 text-sm font-label">
                  <div className="flex justify-between">
                    <span className="text-foreground-600">
                      Segunda a Sexta
                    </span>
                    <span className="text-foreground-900 font-semibold">
                      {s.contactHoursWeek}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-foreground-600">
                      Sábado
                    </span>
                    <span className="text-foreground-900 font-semibold">
                      {s.contactHoursSat}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-foreground-600">
                      Domingo
                    </span>
                    <span className="text-foreground-500 italic">
                      {s.contactHoursSun}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 border-t border-background-200 py-14 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-heading font-bold text-foreground-950 mb-2">
                Nossas Lojas
              </h2>

              <p className="text-foreground-500 text-sm">
                Visite-nos e conheça nossos showrooms completos
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {stores.map((store) => (
                <div
                  key={store.id}
                  className="bg-background-50 border border-background-200 rounded-lg overflow-hidden"
                >
                  <div className="relative h-56">
                    <iframe
                      title={store.name}
                      src={store.mapUrl}
                      className="w-full h-full border-0"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>

                  <div className="p-5">
                    <h3 className="font-heading font-bold text-foreground-950 text-lg mb-3">
                      {store.name}
                    </h3>

                    <div className="space-y-2 text-sm font-label text-foreground-600 mb-4">
                      <p className="flex items-start gap-2">
                        <i className="ri-map-pin-line text-primary-500 mt-0.5 shrink-0" />
                        {store.address}, {store.city}{store.cep ? ` — CEP: ${store.cep}` : ""}
                      </p>

                      <p className="flex items-center gap-2">
                        <i className="ri-phone-line text-primary-500 shrink-0" />
                        {store.phone}
                      </p>

                      <p className="flex items-center gap-2">
                        <i className="ri-time-line text-primary-500 shrink-0" />
                        {store.hours}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <a
                        href={`https://wa.me/${store.whatsapp}`}
                        target="_blank"
                        rel="nofollow noreferrer"
                        className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-background-50 px-4 py-2 rounded-md text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-whatsapp-line" />
                        WhatsApp
                      </a>

                      <a
                        href={store.googleMapsUrl}
                        target="_blank"
                        rel="nofollow noreferrer"
                        className="flex items-center gap-2 bg-foreground-900 hover:bg-primary-500 text-background-50 px-4 py-2 rounded-md text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-map-pin-line" />
                        Google Maps
                      </a>

                      <a
                        href={store.wazeUrl}
                        target="_blank"
                        rel="nofollow noreferrer"
                        className="flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 px-4 py-2 rounded-md text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-navigation-line" />
                        Waze
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}